// src/components/providerAssignments/AssignmentRowActions.tsx
//
// Small kebab menu for a single assignment row. Encapsulates the
// capability checks and dialog orchestration so tables and lists can
// stay skinny. Uses simple details/summary as a portal-free menu —
// works everywhere including inside dialogs and drawers.

import { useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { useHasCapability } from '../../services/centralContext';
import type {
  PersonProviderAssignment,
  ReassignAssignmentResponse,
} from '../../types/api';
import { isTerminalStatus } from './assignmentDisplay';
import AssignmentHistoryDrawer from './AssignmentHistoryDrawer';
import EndAssignmentDialog from './EndAssignmentDialog';
import ReassignmentDialog from './ReassignmentDialog';

export interface AssignmentRowActionsProps {
  assignment: PersonProviderAssignment;
  onChanged?: () => void;
}

export default function AssignmentRowActions({
  assignment,
  onChanged,
}: AssignmentRowActionsProps) {
  const canReassign = useHasCapability('assignments', 'reassign');
  const canEnd = useHasCapability('assignments', 'end');
  const [open, setOpen] = useState(false);
  const [showReassign, setShowReassign] = useState(false);
  const [showEnd, setShowEnd] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const terminal = isTerminalStatus(assignment.status);
  const closeMenu = () => setOpen(false);

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Assignment actions"
        aria-expanded={open}
        className="rounded-md p-1 text-slate-500 hover:bg-slate-100"
        data-testid={`assignment-actions-${assignment.id}`}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-hidden
            className="fixed inset-0 z-10 cursor-default"
            onClick={closeMenu}
          />
          <div
            role="menu"
            className="absolute right-0 z-20 mt-1 w-44 rounded-md border border-slate-200 bg-white text-xs shadow-lg"
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                closeMenu();
                setShowHistory(true);
              }}
              className="block w-full px-3 py-1.5 text-left text-slate-700 hover:bg-slate-50"
            >
              View history
            </button>
            {canReassign && !terminal && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  closeMenu();
                  setShowReassign(true);
                }}
                className="block w-full px-3 py-1.5 text-left text-slate-700 hover:bg-slate-50"
              >
                Reassign provider
              </button>
            )}
            {canEnd && !terminal && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  closeMenu();
                  setShowEnd(true);
                }}
                className="block w-full px-3 py-1.5 text-left text-red-600 hover:bg-red-50"
              >
                End assignment
              </button>
            )}
          </div>
        </>
      )}

      {showReassign && (
        <ReassignmentDialog
          assignment={assignment}
          onClose={() => setShowReassign(false)}
          onReassigned={(_res: ReassignAssignmentResponse) => onChanged?.()}
        />
      )}
      {showEnd && (
        <EndAssignmentDialog
          assignment={assignment}
          onClose={() => setShowEnd(false)}
          onEnded={() => onChanged?.()}
        />
      )}
      {showHistory && (
        <AssignmentHistoryDrawer
          assignment={assignment}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
}
